import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { audioAlerts } from '../../utils/audioAlerts';
import { registerAuthenticatedPushToken } from '../../security/pushApi';

const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;

const PUSH_UNAVAILABLE_KEY = 'ac_push_unavailable';

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}

function isCockpitEmbed(): boolean {
  try {
    return (
      sessionStorage.getItem('ac_cockpit_embed') === '1' ||
      sessionStorage.getItem('ac_impersonate') === 'true'
    );
  } catch {
    return false;
  }
}

function isPushEnvironmentReady(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window) || typeof (window as any).Notification === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (sessionStorage.getItem(PUSH_UNAVAILABLE_KEY) === '1') return false;
  // Localhost: habilita no embed do /simulador (HTTPS) ou se já concedeu permissão
  if (isLocalDevHost()) {
    return isCockpitEmbed() || (window as any).Notification.permission === 'granted';
  }
  return true;
}

async function getFCMToken(): Promise<string> {
  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, getToken } = await import('firebase/messaging');

  const app = getApps().length > 0 ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
  const messaging = getMessaging(app);

  // Reutiliza o Service Worker ativo (/tile-cache-sw.js) e força a verificação de atualização
  const registration = await navigator.serviceWorker.ready;
  try {
    await registration.update();
  } catch (e) {
    console.warn('[PushNotificationManager] Falha ao forçar atualização do SW:', e);
  }
  const token = await getToken(messaging, { 
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration
  });
  if (!token) throw new Error('Token FCM não obtido. Verifique a chave VAPID.');
  return token;
}

export function PushNotificationManager() {
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  const activeListenerRef = useRef(false);
  const pushUnavailableRef = useRef(false);

  // Som no foreground: mobile, iframe estreito do cockpit ou PWA
  const playNotificationBeep = () => {
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobileDevice || isCockpitEmbed() || window.innerWidth < 900) {
      try {
        audioAlerts.playRecebendoNotificacao();
      } catch { /* noop */ }
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user) {
      activeListenerRef.current = false;
      return;
    }

    // Dev local / ambiente sem Push API: não tenta FCM (evita spam AbortError)
    if (!isPushEnvironmentReady()) {
      pushUnavailableRef.current = true;
      return;
    }

    if (typeof window === 'undefined' || !('Notification' in window) || typeof (window as any).Notification === 'undefined') {
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const markUnavailable = () => {
      pushUnavailableRef.current = true;
      try {
        sessionStorage.setItem(PUSH_UNAVAILABLE_KEY, '1');
      } catch {
        // noop
      }
    };

    const setupPush = async () => {
      if (pushUnavailableRef.current || !isPushEnvironmentReady()) return;

      // Se a permissão não for granted, não podemos registrar
      if ((window as any).Notification.permission !== 'granted') {
        return;
      }

      if (activeListenerRef.current) return;
      activeListenerRef.current = true;

      try {
        const fcmToken = await getFCMToken();
        localStorage.setItem('ac_fcm_token', fcmToken);
        const jwtToken = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
        
        if (!jwtToken) return;

        // Sempre registrar o token FCM no backend para garantir sincronização viva (ex: após revogação de chaves)
        const cacheKey = `ac_fcm_registered_${user.id}`;
        try {
          await registerAuthenticatedPushToken(
            {
              fcmToken,
              plataforma: 'WEB',
              tipo: 'desktop_auto',
            },
            { bearerToken: jwtToken },
          );
          localStorage.setItem(cacheKey, fcmToken);
          console.log('[PushNotificationManager] Token FCM sincronizado com sucesso.');
        } catch (err) {
          console.warn('[PushNotificationManager] Falha ao registrar token FCM.', err);
        }

        // Configurar listener de foreground
        const { initializeApp, getApps } = await import('firebase/app');
        const { getMessaging, onMessage } = await import('firebase/messaging');
        const app = getApps().length > 0 ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
        const messaging = getMessaging(app);

        unsubscribe = onMessage(messaging, (payload) => {
          console.log('[PushNotificationManager] Notificação recebida em foreground:', payload);
          
          // Se o app estiver em background/minimizado (escondido), o Firebase SDK ainda assim
          // pode direcionar a mensagem para este listener de primeiro plano em vez do SW de background.
          // Nesse caso, forçamos o Service Worker a exibir o banner nativo para que a notificação apareça.
          if (document.visibilityState === 'hidden') {
            console.log('[PushNotificationManager] App em background. Disparando notificação nativa via SW.');
            navigator.serviceWorker.ready.then((reg) => {
              const notificationTag = payload.data?.solicitacaoId || 'ac-security';
              const title = payload.notification?.title || payload.data?.title || '🔐 Nova solicitação';
              const body = payload.notification?.body || payload.data?.body || 'Você tem uma solicitação pendente.';
              
              reg.showNotification(title, {
                body,
                icon: '/favicon.png',
                badge: '/favicon.png',
                tag: notificationTag,
                data: payload.data,
                ...({ vibrate: [200, 100, 200] } as Record<string, unknown>),
              }).catch((err) => {
                console.error('[PushNotificationManager] Falha ao exibir notificação via SW:', err);
              });
            }).catch((err) => {
              console.error('[PushNotificationManager] Falha ao obter registro do SW:', err);
            });
            return;
          }
          
          playNotificationBeep();

          const title = payload.notification?.title || payload.data?.title || 'Aluno Conectado';
          const body = payload.notification?.body || payload.data?.body || 'Você tem uma nova notificação.';
          const data = payload.data || {};
          // Deep-link para o detalhe da notificação (não só a lista)
          let url = data.url as string | undefined;
          if (!url && data.notificacaoId) {
            url = data.responsavelId
              ? `/responsavel/notificacoes?notificacaoId=${encodeURIComponent(String(data.notificacaoId))}`
              : `/professor/comunicados?notificacaoId=${encodeURIComponent(String(data.notificacaoId))}`;
          }

          const openTarget = () => {
            if (data.notificacaoId) {
              window.dispatchEvent(
                new CustomEvent('ac-open-notificacao', {
                  detail: {
                    id: data.notificacaoId,
                    titulo: title,
                    mensagem: body,
                    tipoNotificacao: data.tipoNotificacao,
                  },
                }),
              );
              return;
            }
            if (url) navigate(url);
          };

          // Banner nativo + toast (cockpit/PWA com app aberto)
          try {
            if (typeof window !== 'undefined' && 'Notification' in window && typeof (window as any).Notification !== 'undefined' && (window as any).Notification.permission === 'granted') {
              const native = new (window as any).Notification(title, {
                body,
                icon: '/icons/icon-192x192.png',
                tag: data.solicitacaoId || data.tipo || data.notificacaoId || 'ac-push',
                data: { ...data, url },
              });
              native.onclick = () => {
                window.focus();
                openTarget();
              };
            }
          } catch { /* noop */ }

          if (url && (
            url.startsWith('/security/approve/') || url.includes('/security/approve/') ||
            url.startsWith('/security/captura-remota/') || url.includes('/security/captura-remota/')
          )) {
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobileDevice || isCockpitEmbed()) {
              navigate(url);
            }
          }

          // Toca som oficial de notificação
          try {
            audioAlerts.playRecebendoNotificacao();
          } catch { /* noop */ }

          // Exibe Toast sonner flutuante na tela
          try {
            toast.info(title || 'Nova notificação recebida', {
              description: body || 'Clique para visualizar os detalhes.',
              duration: 6000,
              action: {
                label: 'Abrir',
                onClick: () => openTarget(),
              },
            });
          } catch { /* noop */ }

          // Avisa sistema global e watchers para exibir banner flutuante e refetch
          try {
            window.dispatchEvent(
              new CustomEvent('ac-push-received', { detail: { title, body, data: { ...data, url } } }),
            );
          } catch { /* noop */ }
        });

        console.log('[PushNotificationManager] Listener de foreground FCM ativado.');
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        const name = error instanceof Error ? error.name : '';
        if (
          name === 'AbortError' ||
          /push service not available/i.test(msg) ||
          /Registration failed/i.test(msg)
        ) {
          markUnavailable();
        } else {
          console.error('[PushNotificationManager] Erro ao configurar push notifications:', error);
        }
        activeListenerRef.current = false;
      }
    };

    setupPush();

    const checkPermissionAndSetup = () => {
      if (pushUnavailableRef.current || !isPushEnvironmentReady()) return;
      if (typeof window !== 'undefined' && 'Notification' in window && typeof (window as any).Notification !== 'undefined' && (window as any).Notification.permission === 'granted' && !activeListenerRef.current) {
        setupPush();
      }
    };

    window.addEventListener('focus', checkPermissionAndSetup);
    const intervalId = setInterval(checkPermissionAndSetup, 4000);

    // Cleanup listener on unmount/auth change
    return () => {
      window.removeEventListener('focus', checkPermissionAndSetup);
      clearInterval(intervalId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isAuthenticated, user]);

  return null;
}
